import { Component, Inject, OnDestroy } from '@angular/core';
import { SensorData, SensorDataGraph } from '../../../../model/sensorData';
import { Subject, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { SnackBarService } from '../../../../services/snackBar.service';
import { SpinnerService } from '../../../../services/spinner.service';
import { DateUtils } from '../../../../utils/dateUtils';
import { DateTime } from 'luxon';

import { ArrayUtils } from '../../../../utils/arrayUtils';
import { EssayUtils } from '../../../../utils/essayUtils';
import * as _ from 'lodash';
import { ExecutionQTestsService } from '../../../../services/execution-q-tests.service';

export interface DialogDataDiscardTimeEdit {
  idExecutionTest: number;
  idEssay: number;
  data: SensorData[];
}


@Component({
    selector: 'app-execution-q-edit-discard-time',
    templateUrl: './execution-q-edit-discard-time.component.html',
    styleUrl: './execution-q-edit-discard-time.component.scss',
    standalone: false,
})
export class ExecutionQEditDiscardTimeComponent implements OnDestroy {

  startDateStr: string;
  endDateStr: string;

  startDateRange: Date;
  endDateRange: Date;

  datasource: SensorDataGraph[];
  sensors: any[];

  private destroy$ = new Subject<void>();

  constructor(
    private dialogRef: MatDialogRef<ExecutionQEditDiscardTimeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogDataDiscardTimeEdit,
    private executionQTestsService: ExecutionQTestsService,
    private translate: TranslateService,
    public snackBarService: SnackBarService,
    private spinnerService: SpinnerService) {

    this.startDateRange = _.cloneDeep(this.data.data.sort((a, b) => a.date.getTime() - b.date.getTime()).find(i => i.date != null).date);
    this.endDateRange = _.cloneDeep(DateUtils.anyToDate(this.startDateRange));
    this.endDateRange.setHours(this.endDateRange.getHours() + 1);

    if (this.startDateStr == null || this.startDateStr === '') {
      this.convertDateToString(this.startDateRange, this.endDateRange);
    }

    this.recalculateDatasource(this.data.data);

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onOkClick(): void {
    const errs = [];

    const startDateTime = DateTime.fromFormat(this.startDateStr, 'dd/MM/yyyy HH:mm:ss');
    this.startDateRange = startDateTime.isValid ? startDateTime.toJSDate() : null;

    const endDateTime = DateTime.fromFormat(this.endDateStr, 'dd/MM/yyyy HH:mm:ss');
    this.endDateRange = endDateTime.isValid ? endDateTime.toJSDate() : null;

    if (this.startDateRange == null) {
      errs.push(this.translate.instant('executionEdit.discardTime.form.error.startDate'));
    }
    if (this.endDateRange == null) {
      errs.push(this.translate.instant('executionEdit.discardTime.form.error.endDate'));
    }

    if (errs.length === 0) {
      this.spinnerService.show();

      this.executionQTestsService.discardTime(this.data.idExecutionTest, this.data.idEssay, this.startDateRange, this.endDateRange)
        .pipe(takeUntil(this.destroy$)).subscribe(res => {
          this.spinnerService.hide();

          this.snackBarService.sendSuccess(this.translate.instant('executionEdit.discardTime.form.save.ok') as string);

          this.dialogRef.close({
            valid: true,
            data: res
          });
        }, () => {
          this.snackBarService.sendError(this.translate.instant('executionEdit.discardTime.form.save.error.generic') as string);
          this.spinnerService.hide();
        });
    } else {
      const error = errs.join('\n');
      this.snackBarService.sendError(error);
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  recalculateDatasource(data: SensorData[]): void {
    this.spinnerService.show();

    if (data == null || ArrayUtils.isEmpty(data)) {
      this.datasource = [];
      this.sensors = [];

      return;
    }

    const res: SensorDataGraph[] = [];
    const sensors = [];

    const groupBy = ArrayUtils.groupBy(data, (item: SensorData) => item.date);
    groupBy.forEach((values: SensorData[], key: Date) => {
      values.forEach(value => {
        const sen = new SensorDataGraph();
        sen.date = key;

        if (!value.equipmentName) {
          return;
        }

        const beautySerialNum = value.equipmentName.replace('-', '').replace(' ', '').toLowerCase();

        sen[beautySerialNum] = value.value;

        const currentSensors = sensors.map(item => item.name);

        if (!currentSensors.includes(value.equipmentName)) {

          sensors.push({
            name: value.equipmentName,
            serialNum: value.serialNum,
            value: beautySerialNum
          });
        }

        res.push(sen);
      });
    });

    this.datasource = res;
    this.sensors = sensors;

    this.data.data.forEach(item => item.date = DateUtils.anyToDate(item.date));

    this.spinnerService.hide();
  }

  onValueChanged = (e) => {
    const changedInit = e.value[0].getTime() !== e.previousValue[0].getTime();
    const changedEnd = e.value[1].getTime() !== e.previousValue[1].getTime();

    let initDate = DateUtils.anyToDate(e.value[0]);
    let endDate = DateUtils.anyToDate(e.value[1]);

    if (changedInit) {
      initDate = EssayUtils.calculateNearDate(initDate, this.data.data);
    }

    if (changedEnd) {
      endDate = EssayUtils.calculateNearDate(endDate, this.data.data);
    }

    this.startDateRange = _.cloneDeep(initDate);
    this.endDateRange = _.cloneDeep(endDate);

    this.convertDateToString(initDate, endDate);
  }

  customizeLabel = (data) => {
    return data.value.toLocaleString();
  }

  customizeSliderMarker = (data) => {
    return data.value.toLocaleString();
  }

  private convertDateToString(startDate: Date, endDate: Date): void {
    try {
      if (startDate == null) {
        this.startDateStr = null;
      } else {
        const date = startDate;
        const start = DateTime.fromJSDate(date).toFormat('dd/MM/yyyy HH:mm:ss');
        this.startDateStr = start;
      }
    } catch (e) {
      this.startDateStr = null;
    }

    try {
      if (endDate == null) {
        this.startDateStr = null;
      } else {
        const date = endDate;
        const end = DateTime.fromJSDate(date).toFormat('dd/MM/yyyy HH:mm:ss');
        this.endDateStr = end;
      }
    } catch (e) {
      this.startDateStr = null;
    }
  }

}
